const EPOCH_CUTOFF_MS = 86_400_000;

export function isMeaningfulDate(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && time >= EPOCH_CUTOFF_MS;
}

export function endOfUtcDay(iso: string): Date {
  const date = new Date(iso);
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );
}

export function resolveListingCutoffMs(event: {
  registrationClosesAt?: string | null;
  endDate?: string | null;
  startDate: string;
}): number {
  if (isMeaningfulDate(event.registrationClosesAt)) {
    return endOfUtcDay(event.registrationClosesAt!).getTime();
  }
  if (isMeaningfulDate(event.endDate)) {
    return new Date(event.endDate!).getTime();
  }
  return endOfUtcDay(event.startDate).getTime();
}

/** True once the event's end date (or start date, if no end date is set) has passed. */
export function hasEventEnded(
  event: { endDate?: string | null; startDate: string },
  now = Date.now(),
): boolean {
  const endMs = isMeaningfulDate(event.endDate)
    ? new Date(event.endDate!).getTime()
    : endOfUtcDay(event.startDate).getTime();
  return now > endMs;
}

export function isEventListedAsUpcoming(
  event: {
    registrationClosesAt?: string | null;
    endDate?: string | null;
    startDate: string;
  },
  now = Date.now(),
): boolean {
  return now <= resolveListingCutoffMs(event);
}

export function deriveRegistrationStatusLabel(
  event: {
    registrationOpensAt?: string | null;
    registrationClosesAt?: string | null;
    endDate?: string | null;
    startDate: string;
    onsiteRegistrationOpen?: boolean;
  },
  now = Date.now(),
): string {
  const cutoff = resolveListingCutoffMs(event);
  if (now > cutoff) {
    return event.onsiteRegistrationOpen ? "Open — On-Site Registration" : "Closed";
  }
  if (
    isMeaningfulDate(event.registrationOpensAt) &&
    now < new Date(event.registrationOpensAt!).getTime()
  ) {
    return "Registration opens soon";
  }
  return "Open for Registration";
}

const DATE_FMT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

export function formatEventDateRange(
  startISO: string,
  endISO: string | null | undefined,
): string {
  const start = new Date(startISO);
  const end = isMeaningfulDate(endISO) ? new Date(endISO!) : null;

  const sameDay =
    end &&
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate();

  if (!end || sameDay) {
    return start.toLocaleDateString("en-GB", DATE_FMT);
  }

  return `${start.toLocaleDateString("en-GB", DATE_FMT)} – ${end.toLocaleDateString(
    "en-GB",
    DATE_FMT,
  )}`;
}

export function formatEventTimeRange(
  startISO: string,
  endISO: string | null | undefined,
): string {
  const start = new Date(startISO);
  const end = isMeaningfulDate(endISO) ? new Date(endISO!) : null;

  const sameDay =
    end &&
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate();

  const fmt: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  if (!end) return start.toLocaleTimeString("en-GB", fmt);
  if (!sameDay) return "";

  return `${start.toLocaleTimeString("en-GB", fmt)} – ${end.toLocaleTimeString(
    "en-GB",
    fmt,
  )}`;
}

export function toDateInputValue(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function dateInputToIso(dateValue: string, endOfDay = false): string {
  const [year, month, day] = dateValue.split("-").map(Number);
  if (endOfDay) {
    return new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999)).toISOString();
  }
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
}
